import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Divider, Stack, Typography } from '@locus-taxy/encore-mui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { EncoreFormRenderer } from '@locus-taxy/encore-form';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode, ViewField } from './profileTypes';
import { ProfileViewRenderer } from './ProfileViewRenderer';
import { buildLocaleSettingsSchema } from './profileFormSchemas';
import { buildTimezoneOptionsWithOffset } from './localeSettingsSectionUtils';

interface LocaleSettingsSectionProps {
  readonly mode: ProfileViewMode;
  readonly clientId: string;
  readonly formRef: React.RefObject<FormRendererRef | null>;
  readonly onChange?: (data: Record<string, unknown>) => void;
  readonly timezone: string;
  readonly timezoneOptions: string[];
  readonly locale: string;
  readonly localeOptions: string[];
  readonly viewFields: ViewField[];
}

function formatDateTimePreview(timezone: string, locale: string): string {
  try {
    const now = new Date();
    const formatted = new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: timezone,
    }).format(now);
    return `${formatted} (${locale})`;
  } catch {
    return '—';
  }
}

function DateTimePreview({
  timezone,
  locale,
}: Readonly<{
  timezone: string;
  locale: string;
}>) {
  const { t } = useTranslation();
  const preview = useMemo(
    () => formatDateTimePreview(timezone, locale),
    [timezone, locale]
  );

  return (
    <Stack spacing={0.5} paddingTop={1}>
      <Typography variant="body2" fontWeight={500} color="text.secondary">
        {t('userDetails.dateTimeFormatPreview', 'Date/Time Format Preview')}
      </Typography>
      <Typography variant="body1" color="text.primary">
        {preview}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {t(
          'userDetails.dateTimeFormatHelp',
          'This is how dates and times will appear across the dashboard.'
        )}
      </Typography>
    </Stack>
  );
}

export function LocaleSettingsSection({
  mode,
  clientId,
  formRef,
  onChange,
  timezone,
  timezoneOptions,
  locale,
  localeOptions,
  viewFields,
}: LocaleSettingsSectionProps) {
  const { t } = useTranslation();

  const [liveTimezone, setLiveTimezone] = useState(timezone);
  const [liveLocale, setLiveLocale] = useState(locale);

  useEffect(() => {
    setLiveTimezone(timezone);
    setLiveLocale(locale);
  }, [timezone, locale, mode]);

  const timezoneSelectOptions = useMemo(
    () =>
      buildTimezoneOptionsWithOffset(timezoneOptions, timezone).map(opt => ({
        label: opt.label,
        value: opt.value,
      })),
    [timezoneOptions, timezone]
  );

  const localeSelectOptions = useMemo(
    () =>
      (localeOptions.length > 0 ? localeOptions : [locale]).map(opt => ({
        label: opt,
        value: opt,
      })),
    [localeOptions, locale]
  );

  const schema = useMemo(
    () =>
      buildLocaleSettingsSchema(
        {
          timezoneOptions: timezoneSelectOptions,
          localeOptions: localeSelectOptions,
        },
        t
      ),
    [timezoneSelectOptions, localeSelectOptions, t]
  );

  const formInitialData = useMemo(
    () => ({ preferences: { timezone, localeIdentifier: locale } }),
    [timezone, locale]
  );

  const handleFormChange = useCallback(
    (data: Record<string, unknown>) => {
      const prefs = data.preferences as Record<string, unknown> | undefined;
      if (prefs) {
        if (typeof prefs.timezone === 'string') {
          setLiveTimezone(prefs.timezone);
        }
        if (typeof prefs.localeIdentifier === 'string') {
          setLiveLocale(prefs.localeIdentifier);
        }
      }
      onChange?.(data);
    },
    [onChange]
  );

  const previewTimezone = mode === 'edit' ? liveTimezone : timezone;
  const previewLocale = mode === 'edit' ? liveLocale : locale;

  return (
    <Card
      elevation={0}
      variant="outlined"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'locale-settings-section',
        page: 'account',
      })}
    >
      <Stack spacing={3} padding={4}>
        <Stack spacing={0.5}>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t('userDetails.localeSettings', 'Locale Settings')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'userDetails.localeDescription',
              'These settings will reflect changes across the dashboard.'
            )}
          </Typography>
        </Stack>

        {mode === 'view' ? (
          <Stack spacing={3}>
            <ProfileViewRenderer fields={viewFields} columns={2} />
            <Divider />
            <DateTimePreview timezone={timezone} locale={locale} />
          </Stack>
        ) : (
          <>
            <EncoreFormRenderer
              ref={formRef}
              schema={schema as any}
              clientId={clientId}
              initialData={formInitialData}
              onChange={handleFormChange}
              routeContext={{ entity: 'user', page: 'profile' }}
            />
            <DateTimePreview
              timezone={previewTimezone}
              locale={previewLocale}
            />
          </>
        )}
      </Stack>
    </Card>
  );
}
